import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Card, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

const WINDOW_SIZE = 24;
const POLL_MS = 2000;

const METRICS = [
  {
    key: "latency",
    title: "Latency p95",
    beforeKey: "latencyBefore",
    afterKey: "latencyAfter",
    suffix: " ms",
    colorBefore: "#f97316",
    colorAfter: "#22c55e",
  },
  {
    key: "error",
    title: "Error rate",
    beforeKey: "errorBefore",
    afterKey: "errorAfter",
    suffix: "%",
    colorBefore: "#ef4444",
    colorAfter: "#22c55e",
  },
  {
    key: "failed",
    title: "Failed requests / hour",
    beforeKey: "failedBefore",
    afterKey: "failedAfter",
    suffix: "",
    colorBefore: "#f59e0b",
    colorAfter: "#22c55e",
  },
  {
    key: "cost",
    title: "Infrastructure cost",
    beforeKey: "costBefore",
    afterKey: "costAfter",
    suffix: "k EUR",
    colorBefore: "#a78bfa",
    colorAfter: "#22c55e",
  },
];

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function tsLabel(d) {
  const dt = new Date(d);
  const h = `${dt.getHours()}`.padStart(2, "0");
  const m = `${dt.getMinutes()}`.padStart(2, "0");
  return `${h}:${m}`;
}

function makeMockPoint(last) {
  const now = Date.now();
  const drift = last ? Math.sin(now / 9000) * 6 : 0;

  const latencyBefore = clamp((last?.latencyBefore ?? 520) + drift + (Math.random() - 0.5) * 20, 350, 680);
  const latencyAfter = clamp((last?.latencyAfter ?? 110) + drift * 0.35 + (Math.random() - 0.5) * 8, 80, 190);

  const errorBefore = clamp((last?.errorBefore ?? 5.2) + (Math.random() - 0.5) * 0.55, 3.2, 8.6);
  const errorAfter = clamp((last?.errorAfter ?? 0.35) + (Math.random() - 0.5) * 0.10, 0.12, 0.90);

  const failedBefore = clamp((last?.failedBefore ?? 160) + (Math.random() - 0.5) * 18, 95, 230);
  const failedAfter = clamp((last?.failedAfter ?? 10) + (Math.random() - 0.5) * 3, 4, 24);

  const costBefore = clamp((last?.costBefore ?? 42) + (Math.random() - 0.5) * 1.2, 36, 48);
  const costAfter = clamp((last?.costAfter ?? 34) + (Math.random() - 0.5) * 0.8, 29, 38);

  return {
    ts: now,
    latencyBefore,
    latencyAfter,
    errorBefore,
    errorAfter,
    failedBefore,
    failedAfter,
    costBefore,
    costAfter,
  };
}

function makeMockSeries() {
  const pts = [];
  let last = null;
  for (let i = WINDOW_SIZE - 1; i >= 0; i -= 1) {
    const p = makeMockPoint(last);
    p.ts = Date.now() - i * POLL_MS;
    pts.push(p);
    last = p;
  }
  return pts;
}

function linePath(points, key, minV, maxV, w, h, pad) {
  if (!points.length) return "";
  const usableW = w - pad * 2;
  const usableH = h - pad * 2;
  const den = Math.max(1, points.length - 1);

  return points
    .map((p, i) => {
      const x = pad + (i / den) * usableW;
      const norm = (p[key] - minV) / Math.max(1e-9, maxV - minV);
      const y = h - pad - norm * usableH;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

function yForValue(value, minV, maxV, h, pad) {
  const usableH = h - pad * 2;
  const norm = (value - minV) / Math.max(1e-9, maxV - minV);
  return h - pad - norm * usableH;
}

function formatMetricValue(v, suffix) {
  const n = Number(v);
  if (Number.isNaN(n)) return "--";
  if (suffix === "%") return `${n.toFixed(2)}%`;
  if (suffix.trim() === "ms") return `${Math.round(n)} ms`;
  if (suffix === "k EUR") return `${n.toFixed(1)}k`;
  return `${Math.round(n)}`;
}

function ChartLegend({ colorBefore, colorAfter }) {
  const { t } = useTranslation();
  return (
    <div className="d-flex gap-3 small mt-1">
      <span style={{ color: colorBefore }}>{t('demo.chartBefore')}</span>
      <span style={{ color: colorAfter }}>{t('demo.chartAfter')}</span>
    </div>
  );
}

function TimelineChart({ points, metric }) {
  const width = 680;
  const height = 190;
  const pad = 20;

  const vals = points.flatMap((p) => [p[metric.beforeKey], p[metric.afterKey]]);
  const minV = vals.length ? Math.min(...vals) : 0;
  const maxV = vals.length ? Math.max(...vals) : 1;

  const beforePath = linePath(points, metric.beforeKey, minV, maxV, width, height, pad);
  const afterPath = linePath(points, metric.afterKey, minV, maxV, width, height, pad);
  const last = points[points.length - 1];
  const lastBefore = last ? Number(last[metric.beforeKey]) : NaN;
  const lastAfter = last ? Number(last[metric.afterKey]) : NaN;
  const lastBeforeY = yForValue(lastBefore, minV, maxV, height, pad);
  const lastAfterY = yForValue(lastAfter, minV, maxV, height, pad);
  const lastX = width - pad;
  const beforeLabelY = Math.max(14, Math.min(height - 8, lastBeforeY - 8));
  const afterLabelY = Math.max(14, Math.min(height - 8, lastAfterY - 8));

  return (
    <div className="demo-chart-wrap" style={{ width: "100%" }}>
      <div className="d-flex justify-content-between align-items-center mb-1 small">
        <span className="text-muted">{metric.title}</span>
        <span className="text-muted">unit: {metric.suffix || "count"}</span>
      </div>

      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="190" role="img" aria-label={`${metric.title} timeline`}>
        <rect x="0" y="0" width={width} height={height} fill="transparent" />

        {[0, 1, 2, 3].map((i) => {
          const y = pad + (i * (height - pad * 2)) / 3;
          return <line key={i} x1={pad} y1={y} x2={width - pad} y2={y} stroke="rgba(0,0,0,0.08)" strokeWidth="1" />;
        })}

        <path d={beforePath} fill="none" stroke={metric.colorBefore} strokeWidth="2.5" strokeLinecap="round" />
        <path d={afterPath} fill="none" stroke={metric.colorAfter} strokeWidth="2.5" strokeLinecap="round" />

        {!Number.isNaN(lastBefore) ? (
          <>
            <circle cx={lastX} cy={lastBeforeY} r="9.2" fill={metric.colorBefore} />
            <text
              x={lastX - 6}
              y={beforeLabelY}
              textAnchor="end"
              fill={metric.colorBefore}
              fontSize="24"
              fontWeight="900"
              stroke="rgba(255,255,255,0.85)"
              strokeWidth="1.2"
              paintOrder="stroke"
            >
              {formatMetricValue(lastBefore, metric.suffix)}
            </text>
          </>
        ) : null}

        {!Number.isNaN(lastAfter) ? (
          <>
            <circle cx={lastX} cy={lastAfterY} r="9.2" fill={metric.colorAfter} />
            <text
              x={lastX - 6}
              y={afterLabelY + 12}
              textAnchor="end"
              fill={metric.colorAfter}
              fontSize="24"
              fontWeight="900"
              stroke="rgba(255,255,255,0.85)"
              strokeWidth="1.2"
              paintOrder="stroke"
            >
              {formatMetricValue(lastAfter, metric.suffix)}
            </text>
          </>
        ) : null}

        <text x={pad} y={height - 4} fill="rgba(0,0,0,0.4)" fontSize="10">
          {points[0] ? tsLabel(points[0].ts) : ""}
        </text>
        <text x={width - pad} y={height - 4} textAnchor="end" fill="rgba(0,0,0,0.4)" fontSize="10">
          {points[points.length - 1] ? tsLabel(points[points.length - 1].ts) : ""}
        </text>
      </svg>

      <ChartLegend colorBefore={metric.colorBefore} colorAfter={metric.colorAfter} />
    </div>
  );
}

export default function HealthDemoSection() {
  const { t } = useTranslation();
  const [timeline, setTimeline] = useState(makeMockSeries());
  const [isMobile, setIsMobile] = useState(false);
  const [showAllCharts, setShowAllCharts] = useState(false);
  const [snapshot, setSnapshot] = useState({
    beforeStatus: "unstable production",
    afterStatus: "stable production",
    systemName: "Payment Platform",
    uptime: "14d 02h",
    latencyBefore: 520,
    latencyAfter: 110,
    errorBefore: 5.2,
    errorAfter: 0.35,
    failedBefore: 160,
    failedAfter: 10,
    leadTimeBefore: 16,
    leadTimeAfter: 4,
    costBefore: 42,
    costAfter: 34,
    note: "Before = unstable baseline. After = optimized stable state.",
  });
  const [loading, setLoading] = useState(true);

  const timerRef = useRef(null);

  const reduction = useMemo(() => {
    const lat = ((snapshot.latencyBefore - snapshot.latencyAfter) / snapshot.latencyBefore) * 100;
    const err = ((snapshot.errorBefore - snapshot.errorAfter) / snapshot.errorBefore) * 100;
    const fail = ((snapshot.failedBefore - snapshot.failedAfter) / snapshot.failedBefore) * 100;
    return {
      lat: `${lat.toFixed(0)}%`,
      err: `${err.toFixed(0)}%`,
      fail: `${fail.toFixed(0)}%`,
    };
  }, [snapshot]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setShowAllCharts(true);
    } else {
      setShowAllCharts(false);
    }
  }, [isMobile]);

  useEffect(() => {
    setLoading(false);

    timerRef.current = setInterval(() => {
      setTimeline((prev) => {
        const next = [...prev, makeMockPoint(prev[prev.length - 1])].slice(-WINDOW_SIZE);
        const last = next[next.length - 1];

        setSnapshot((prevSnap) => ({
          ...prevSnap,
          beforeStatus: "unstable production",
          afterStatus: "stable production",
          latencyBefore: Number(last.latencyBefore),
          latencyAfter: Number(last.latencyAfter),
          errorBefore: Number(last.errorBefore),
          errorAfter: Number(last.errorAfter),
          failedBefore: Number(last.failedBefore),
          failedAfter: Number(last.failedAfter),
          costBefore: Number(last.costBefore),
          costAfter: Number(last.costAfter),
          note: "Before = unstable baseline. After = optimized stable state.",
        }));

        return next;
      });
    }, POLL_MS);

    return () => clearInterval(timerRef.current);
  }, []);

  const visibleMetrics = isMobile && !showAllCharts ? METRICS.slice(0, 1) : METRICS;

  return (
    <section id="demo" className="health-section">
      <div className="container py-5">
        <div className="row align-items-flex-start g-4">
          <div className="col-lg-4">
            <h2 className="mb-3">{t('demo.heading')}</h2>
            <p className="text-muted demo-lead">{t('demo.lead')}</p>

            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
              <Badge bg={snapshot.beforeStatus === "stable production" ? "success" : "danger"}>
                {t('demo.before')}: {snapshot.beforeStatus}
              </Badge>
              <Badge bg={snapshot.afterStatus === "stable production" ? "success" : "danger"}>
                {t('demo.after')}: {snapshot.afterStatus}
              </Badge>
            </div>

            <div className="demo-notes">
              <div className="small text-muted mb-2">{t('demo.businessImpact')}</div>
              <ul className="small text-muted">
                <li>{t('demo.latencyReduction')}: <strong>{reduction.lat}</strong></li>
                <li>{t('demo.errorReduction')}: <strong>{reduction.err}</strong></li>
                <li>{t('demo.failedReduction')}: <strong>{reduction.fail}</strong></li>
              </ul>
            </div>
          </div>

          <div className="col-lg-8">
            <Card className="terminal-card shadow-lg">
              <Card.Header className="terminal-header d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <span className="dot dot-red" />
                  <span className="dot dot-yellow" />
                  <span className="dot dot-green" />
                  <span className="terminal-title">payment-health timelines</span>
                </div>
                <span className="small text-muted">4 metrics / live window</span>
              </Card.Header>

              <Card.Body className="terminal-body">
                {loading ? (
                  <div className="small text-muted">{t('demo.loading')}</div>
                ) : (
                  <>
                    <Row className="g-3">
                      {visibleMetrics.map((m) => (
                        <Col md={6} key={m.key}>
                          <TimelineChart points={timeline} metric={m} />
                        </Col>
                      ))}
                    </Row>

                    {isMobile && !showAllCharts ? (
                      <div className="mt-3 text-center">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowAllCharts(true)}>
                          {t('demo.showMore')}
                        </button>
                      </div>
                    ) : null}

                    <Row className="g-3 mt-2">
                      <Col md={6}>
                        <div className="metric-card">
                          <div className="metric-title">{t('demo.leadTime')}</div>
                          <div className="metric-sub">Before: <strong>{snapshot.leadTimeBefore}h</strong></div>
                          <div className="metric-sub">After: <strong>{snapshot.leadTimeAfter}h</strong></div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="metric-card">
                          <div className="metric-title">{t('demo.infraCost')}</div>
                          <div className="metric-sub">Before: <strong>{snapshot.costBefore.toFixed(1)}k EUR</strong></div>
                          <div className="metric-sub">After: <strong>{snapshot.costAfter.toFixed(1)}k EUR</strong></div>
                        </div>
                      </Col>
                    </Row>
                  </>
                )}
              </Card.Body>
            </Card>
          </div>
        </div>

        <div id="rag-chat-lab" className="row mt-4">
          <div className="col-12">
            <Card className="rag-lab-card border-0">
              <Card.Body className="rag-lab-body p-4 p-md-5">
                <div className="rag-lab-kicker">{t('demo.rag.kicker')}</div>
                <h3 className="rag-lab-title mb-2">{t('demo.rag.heading')}</h3>
                <p className="rag-lab-lead mb-3">{t('demo.rag.lead')}</p>
                <p className="rag-lab-copy mb-4">{t('demo.rag.copy')}</p>
                <div className="rag-lab-tags">
                  {t('demo.rag.tags', { returnObjects: true }).map((tag, i) => (
                    <span key={i}>{tag}</span>
                  ))}
                </div>
                <div className="rag-lab-actions mt-4">
                  <a className="btn rag-lab-btn-soft" href="https://rag.aprelkin.com" target="_blank" rel="noreferrer">
                    {t('demo.rag.btn')}
                  </a>
                  <span className="rag-lab-note">{t('demo.rag.note')}</span>
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}

